// 控制面 API 客户端：带 Bearer Token 的 fetch 封装
import type {
  DataSource, Strategy, Trader, Setting, Revision,
  ValidateResult, ApplyResult, StatusInfo,
  EtfIndex, EtfFund, EtfDaily,
  InstitutionalSignal,
} from "./types";

const LS_BASE = "quant.apiBase";
const LS_TOKEN = "quant.apiToken";
const SS_TOKEN = "quant.apiToken.session";

const DEFAULT_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) || "http://127.0.0.1:8787";

export function getApiBase(): string {
  return localStorage.getItem(LS_BASE) || DEFAULT_BASE;
}
export function setApiBase(v: string) {
  localStorage.setItem(LS_BASE, v.replace(/\/+$/, ""));
}
export function getToken(): string {
  const current = sessionStorage.getItem(SS_TOKEN);
  if (current) return current;
  // 从旧版本迁移一次，之后 Token 只保留到当前浏览器会话结束。
  const legacy = localStorage.getItem(LS_TOKEN);
  if (!legacy) return "";
  sessionStorage.setItem(SS_TOKEN, legacy);
  localStorage.removeItem(LS_TOKEN);
  return legacy;
}
export function setToken(v: string) {
  localStorage.removeItem(LS_TOKEN);
  if (v) sessionStorage.setItem(SS_TOKEN, v);
  else sessionStorage.removeItem(SS_TOKEN);
}
export function clearToken() {
  localStorage.removeItem(LS_TOKEN);
  sessionStorage.removeItem(SS_TOKEN);
}

export class ApiError extends Error {
  status: number;
  detail: unknown;
  constructor(status: number, detail: unknown) {
    super(typeof detail === "string" ? detail : JSON.stringify(detail));
    this.status = status;
    this.detail = detail;
  }
}

async function req<T>(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const res = await fetch(getApiBase() + path, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    if (res.status === 401) window.dispatchEvent(new Event("quant:unauthorized"));
    throw new ApiError(res.status, (data && data.detail) ?? data ?? res.statusText);
  }
  return data as T;
}

export const api = {
  health: () => req<{ ok: boolean }>("GET", "/api/health"),
  status: () => req<StatusInfo>("GET", "/api/status"),

  signals: (staleAfterSeconds = 5, signal?: AbortSignal) =>
    req<InstitutionalSignal[]>(
      "GET", `/api/runtime/signals?stale_after_seconds=${staleAfterSeconds}`, undefined, signal,
    ),
  signalHistory: (symbol: string, limit = 100, signal?: AbortSignal) =>
    req<InstitutionalSignal[]>(
      "GET",
      `/api/runtime/signals/${encodeURIComponent(symbol)}/history?limit=${limit}`,
      undefined,
      signal,
    ),

  // 数据源
  listDataSources: () => req<DataSource[]>("GET", "/api/config/data-sources"),
  upsertDataSource: (name: string, body: Pick<DataSource, "kind" | "params" | "enabled">) =>
    req<DataSource>("PUT", `/api/config/data-sources/${encodeURIComponent(name)}`, body),
  deleteDataSource: (name: string) =>
    req<{ deleted: string }>("DELETE", `/api/config/data-sources/${encodeURIComponent(name)}`),

  // 策略
  listStrategies: () => req<Strategy[]>("GET", "/api/config/strategies"),
  upsertStrategy: (
    name: string,
    body: Pick<Strategy, "class_path" | "symbol" | "trader_ref" | "group_name" | "params" | "enabled">,
  ) => req<Strategy>("PUT", `/api/config/strategies/${encodeURIComponent(name)}`, body),
  deleteStrategy: (name: string) =>
    req<{ deleted: string }>("DELETE", `/api/config/strategies/${encodeURIComponent(name)}`),
  hotUpdateParams: (name: string, changes: Record<string, unknown>) =>
    req<{ applied: Record<string, unknown> }>(
      "POST", `/api/strategies/${encodeURIComponent(name)}/params`, { changes }),

  // 执行器
  listTraders: () => req<Trader[]>("GET", "/api/config/traders"),
  upsertTrader: (name: string, body: Pick<Trader, "type" | "params">) =>
    req<Trader>("PUT", `/api/config/traders/${encodeURIComponent(name)}`, body),
  deleteTrader: (name: string) =>
    req<{ deleted: string }>("DELETE", `/api/config/traders/${encodeURIComponent(name)}`),

  // 设置
  listSettings: () => req<Setting[]>("GET", "/api/config/settings"),
  upsertSetting: (key: string, value: Record<string, unknown>) =>
    req<Setting>("PUT", `/api/config/settings/${encodeURIComponent(key)}`, { value }),

  // 校验 / 应用 / 版本
  validate: () => req<ValidateResult>("POST", "/api/config/validate"),
  apply: (note: string) => req<ApplyResult>("POST", "/api/config/apply", { note }),
  revisions: (limit = 50) => req<Revision[]>("GET", `/api/config/revisions?limit=${limit}`),

  // ETF 分析
  etfIndices: (signal?: AbortSignal) =>
    req<EtfIndex[]>("GET", "/api/analytics/etf/indices", undefined, signal),
  etfFunds: (trackingIndex: string, signal?: AbortSignal) =>
    req<EtfFund[]>(
      "GET",
      `/api/analytics/etf/funds?tracking_index=${encodeURIComponent(trackingIndex)}`,
      undefined,
      signal,
    ),
  etfDaily: (
    fundCode: string,
    range: { start?: string; end?: string },
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams();
    if (range.start) query.set("start", range.start);
    if (range.end) query.set("end", range.end);
    const suffix = query.size ? `?${query.toString()}` : "";
    return req<EtfDaily>(
      "GET",
      `/api/analytics/etf/funds/${encodeURIComponent(fundCode)}/daily${suffix}`,
      undefined,
      signal,
    );
  },
};

export async function streamSignals(
  onEvent: (event: "snapshot" | "signal", data: InstitutionalSignal[] | InstitutionalSignal) => void,
  signal: AbortSignal,
): Promise<void> {
  const headers: Record<string, string> = { Accept: "text/event-stream" };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${getApiBase()}/api/runtime/signals/stream`, { headers, signal });
  if (!res.ok || !res.body) {
    if (res.status === 401) window.dispatchEvent(new Event("quant:unauthorized"));
    throw new ApiError(res.status, res.statusText || "实时信号流不可用");
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) return;
    buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");
    let boundary = buffer.indexOf("\n\n");
    while (boundary >= 0) {
      const block = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      let event = "message";
      const data: string[] = [];
      for (const line of block.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        if (line.startsWith("data:")) data.push(line.slice(5).trimStart());
      }
      if ((event === "snapshot" || event === "signal") && data.length) {
        onEvent(event, JSON.parse(data.join("\n")));
      }
      boundary = buffer.indexOf("\n\n");
    }
  }
}
